import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  RefreshCw,
  Video,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminToken } from "@/context/AdminTokenContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtSlot(iso) {
  if (!iso) return "No slot";
  const [d, t] = iso.split("T");
  const [h, m] = t.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${d} · ${hour}:${m} ${ampm}`;
}

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

const StatusBadge = ({ status }) => (
  <span
    className={`text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1 ${STATUS_STYLES[status] || STATUS_STYLES.cancelled}`}
    data-testid="booking-status-badge"
  >
    {status}
  </span>
);

const BookingCard = ({ b }) => {
  const isMeet = b.contact_method === "google_meet";
  return (
    <li
      className="p-5 md:p-6 space-y-3"
      data-testid={`booking-${b.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-serif text-lg text-[var(--gg-deep)] font-semibold">
          {b.full_name}
          {b.senior_name && (
            <span className="text-sm text-[var(--gg-text-muted)] font-body font-normal ml-2">
              (for {b.senior_name})
            </span>
          )}
        </div>
        <StatusBadge status={b.payment_status} />
      </div>
      <div className="text-sm text-[var(--gg-deep)] font-medium" data-testid="booking-slot">
        {fmtSlot(b.slot_datetime)} <span className="text-[var(--gg-text-muted)]">(Jamaica time)</span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--gg-text)]">
        <span className="inline-flex items-center gap-1.5">
          <Mail size={13} className="text-[var(--gg-primary)]" /> {b.email}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Phone size={13} className="text-[var(--gg-primary)]" /> {b.phone}
        </span>
        <span className="inline-flex items-center gap-1.5">
          {isMeet ? (
            <Video size={13} className="text-[var(--gg-primary)]" />
          ) : (
            <MessageCircle size={13} className="text-[var(--gg-primary)]" />
          )}
          {isMeet ? "Google Meet" : "WhatsApp"}
        </span>
        <span className="text-[var(--gg-text-muted)]">
          {(b.service_interest || "").replace(/_/g, " ")} · {(b.relationship || "").replace(/_/g, " ")}
        </span>
      </div>
      {b.message && (
        <p className="text-sm text-[var(--gg-text-muted)] italic border-l-2 border-[var(--gg-border)] pl-3">
          {b.message}
        </p>
      )}
      {(b.meet_link || b.google_event_link) && (
        <div className="flex flex-wrap gap-3 pt-1">
          {b.meet_link && (
            <a
              href={b.meet_link}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-meet-link"
              className="inline-flex items-center gap-1.5 text-sm text-white bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] rounded-full px-4 py-1.5"
            >
              <Video size={14} /> Join Meet
            </a>
          )}
          {b.google_event_link && (
            <a
              href={b.google_event_link}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-event-link"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--gg-primary)] hover:text-[var(--gg-primary-dark)] font-semibold"
            >
              <ExternalLink size={14} /> Calendar event
            </a>
          )}
        </div>
      )}
    </li>
  );
};

const FILTERS = [
  { v: "paid", label: "Paid" },
  { v: "pending", label: "Pending" },
  { v: "all", label: "All" },
];

export default function AdminBookingsPage() {
  const { token, setToken } = useAdminToken();
  const [bookings, setBookings] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("paid");
  const autoLoaded = useRef(false);

  const load = useCallback(async () => {
    if (!token) {
      toast.error("Enter your admin token first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/consultations?limit=200`, {
        headers: { "X-Admin-Token": token },
      });
      setBookings(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token.");
      } else {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && !autoLoaded.current) {
      autoLoaded.current = true;
      load();
    }
  }, [token, load]);

  const visible = (bookings || []).filter(
    (b) => filter === "all" || b.payment_status === filter
  );

  return (
    <div className="bg-[var(--gg-surface)] min-h-screen py-12" data-testid="admin-bookings-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-[var(--gg-text-muted)] hover:text-[var(--gg-primary)] mb-6"
        >
          <ArrowLeft size={16} /> Admin hub
        </Link>
        <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
          Consultation Bookings — Admin
        </h1>
        <p className="text-[var(--gg-text)] mt-2">
          Upcoming and past consultation bookings, with payment status and
          Google Meet links.
        </p>

        <div className="mt-8 bg-white border border-[var(--gg-border)] rounded-2xl p-6">
          <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold">
            Admin token
          </Label>
          <div className="flex gap-2 mt-2">
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Paste your X-Admin-Token"
              data-testid="bookings-token-input"
            />
            <button
              onClick={load}
              disabled={loading}
              className="bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 text-white rounded-full px-5 text-sm inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="bookings-load"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              {bookings === null ? "Load bookings" : "Refresh"}
            </button>
          </div>
          <p className="text-xs text-[var(--gg-text-muted)] mt-2">
            Held in memory only — never stored in the browser. Tip: unlock once
            at the <Link to="/admin" className="text-[var(--gg-primary)] underline">admin hub</Link> to
            share it across all admin pages.
          </p>
        </div>

        {bookings !== null && (
          <>
            <div className="mt-8 flex items-center gap-2" data-testid="bookings-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.v}
                  onClick={() => setFilter(f.v)}
                  data-testid={`bookings-filter-${f.v}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    filter === f.v
                      ? "bg-[var(--gg-deep)] text-white"
                      : "bg-white border border-[var(--gg-border)] text-[var(--gg-text)] hover:border-[var(--gg-primary)]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="ml-auto text-sm text-[var(--gg-text-muted)]" data-testid="bookings-count">
                {visible.length} booking{visible.length === 1 ? "" : "s"}
              </span>
            </div>

            <ul className="mt-4 divide-y divide-[var(--gg-border)] bg-white rounded-2xl border border-[var(--gg-border)]" data-testid="bookings-list">
              {visible.length === 0 && (
                <li className="p-6 text-sm text-[var(--gg-text-muted)]" data-testid="bookings-empty">
                  No {filter === "all" ? "" : `${filter} `}bookings yet.
                </li>
              )}
              {visible.map((b) => (
                <BookingCard key={b.id} b={b} />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
